// "अभ्यास": three quick questions on the page, "(अहं) हस + ? → which ending?". The options are ending chips from the
// engine (distractors are endings of other cells or tenses that are not valid for this cell); the answer is graded on
// the server (POST /api/grammar/pratyaya), which also returns the finished form. A self-check: no XP.

import type { FC, ReactNode } from 'react'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { PratyayaTable } from '@/prakrit/lib/grammar/pratyaya'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, Check, RotateCcw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBER_LABEL, PERSON_LABEL, TENSE_SHORT } from '@/prakrit/lib/grammar/labels'
import { endingLabel, pratyayaTable, sameInEveryCell } from '@/prakrit/lib/grammar/pratyaya'
import { hashString, seededShuffle } from '@/prakrit/lib/grammar/questions'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { SECONDARY_BUTTON } from '@/prakrit/components/grammar/labs/lab-ui'
import { subjectsOf } from '@/prakrit/components/grammar/kal/kal-data'

interface PratyayaQuizProps {
    root: string
    tense: Tense
    table: PratyayaTable
    /** Where the graded games for this tense live. */
    practiceHref: string
}

interface Question {
    key: string
    person: Person
    number: Num
    options: string[]
}

type Result = { status: 'checking' } | { status: 'done', correct: boolean, given: string, expected: string[], form: string | null, forms: string[] } | { status: 'error' }

/** Tenses whose endings make good look-alikes for each other. */
const FAMILY: Record<string, Tense[]> = {
    basic: ['vartaman', 'vidhi', 'bhoot', 'bhavishya'],
    karmani: ['karmani_vartaman', 'karmani_vidhi', 'karmani_bhoot'],
    prerak: ['prerak_vartaman', 'prerak_vidhi', 'prerak_bhavishya', 'prerak_bhoot']
}

const familyOf = (tense: Tense): Tense[] => {
    if (tense.startsWith('karmani_')) return FAMILY.karmani
    if (tense.startsWith('prerak_')) return FAMILY.prerak

    return FAMILY.basic
}

const CELLS: [Person, Num][] = [['uttam', 'eka'], ['madhyam', 'bahu'], ['anya', 'bahu']]

const buildQuestions = (root: string, tense: Tense, table: PratyayaTable): Question[] => {
    const others = familyOf(tense).filter(t => t !== tense).map(t => pratyayaTable(t, root)).filter((t): t is PratyayaTable => t !== null)
    const cells: [Person, Num][] = sameInEveryCell(table) ? [['uttam', 'eka']] : CELLS

    return cells.flatMap(([p, n]) => {
        const cell = table[p][n]
        const right = cell.samanya[0]

        if (!right) return []

        const valid = new Set([...cell.samanya, ...cell.vikalp].map(endingLabel))
        const pool: string[] = []

        for (const person of PERSONS) {
            for (const num of NUMBERS) {
                for (const f of table[person][num].samanya) pool.push(endingLabel(f))
            }
        }

        for (const t of others) for (const f of t[p][n].samanya) pool.push(endingLabel(f))

        const distractors = Array.from(new Set(pool.filter(e => !valid.has(e)))).slice(0, 2)

        if (distractors.length < 2) return []

        const key = `${root}|${tense}|${p}.${n}`

        return [{ key, person: p, number: n, options: seededShuffle([endingLabel(right), ...distractors], hashString(key)) }]
    })
}

const QuestionCard: FC<{ index: number, root: string, tense: Tense, q: Question }> = ({ index, root, tense, q }): ReactNode => {
    const [result, setResult] = useState<Result | null>(null)
    const subjects = subjectsOf(q.person, q.number).book
    const answered = result?.status === 'done'

    const check = async (ending: string) => {
        setResult({ status: 'checking' })

        try {
            const res = await fetch('/api/grammar/pratyaya', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ root, tense, person: q.person, number: q.number, ending })
            })

            if (!res.ok) throw new Error(String(res.status))

            const data = await res.json() as { correct: boolean, expected: string[], form: string | null, forms: string[] }

            setResult({ status: 'done', correct: data.correct, given: ending, expected: data.expected, form: data.form, forms: data.forms })
        }
        catch {
            setResult({ status: 'error' })
        }
    }

    return (
        <li className={cn(PANEL, 'flex flex-col gap-3 p-5')}>
            <p className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                <span className='text-sm font-semibold text-muted-foreground'>{index + 1}.</span>
                <span lang='pra' className='font-prakrit text-xl font-semibold'>
                    ({subjects.slice(0, 2).join(', ')}) {root} + ___
                </span>
                <span className='text-sm text-muted-foreground'>
                    <span lang='hi' className='hindi'>{TENSE_SHORT[tense].hi}, {PERSON_LABEL[q.person].hi} {NUMBER_LABEL[q.number].hi}</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>{TENSE_SHORT[tense].en}</span>
                </span>
            </p>

            <div role='group' aria-label='प्रत्यय चुनें · Choose the ending' className='flex flex-wrap gap-2'>
                {q.options.map(option => {
                    const chosen = answered && result.given === option
                    const right = answered && result.expected.includes(option)

                    return (
                        <button
                            key={option}
                            type='button'
                            lang='pra'
                            disabled={answered || result?.status === 'checking'}
                            aria-pressed={chosen}
                            onClick={() => check(option)}
                            className={cn(
                                'inline-flex min-h-tap min-w-14 items-center justify-center rounded-full border-2 px-4 font-prakrit text-lg font-semibold transition-colors',
                                'disabled:cursor-default',
                                !answered && 'border-border bg-card hover:border-saffron/70 hover:bg-saffron/8',
                                answered && right && 'border-success bg-success-soft text-success',
                                answered && chosen && !right && 'border-danger bg-danger-soft text-danger',
                                answered && !chosen && !right && 'border-border bg-card opacity-60'
                            )}
                        >
                            {option}
                        </button>
                    )
                })}
            </div>

            <div aria-live='polite' className='min-h-6 text-sm'>
                {result?.status === 'error' && (
                    <p className='text-danger'>
                        <span lang='hi' className='hindi'>उत्तर जाँचा नहीं जा सका। फिर कोशिश करें।</span>
                        <span aria-hidden='true'> · </span>
                        <span lang='en'>The answer could not be checked. Please try again.</span>
                    </p>
                )}
                {answered && (
                    <p className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold', result.correct ? 'text-success' : 'text-danger')}>
                        {result.correct ? <Check className='size-4' aria-hidden='true' /> : <X className='size-4' aria-hidden='true' />}
                        <span lang='hi' className='hindi'>{result.correct ? 'सही उत्तर!' : `सही प्रत्यय: ${result.expected.join(' / ')}`}</span>
                        <span aria-hidden='true' className='font-normal text-muted-foreground'>·</span>
                        <span lang='pra' className='font-prakrit text-base text-prakrit'>{root} → {result.form ?? result.forms.join(' / ')}</span>
                    </p>
                )}
            </div>

            {(answered || result?.status === 'error') && (
                <button type='button' onClick={() => setResult(null)} className={cn(SECONDARY_BUTTON, 'self-start')}>
                    <RotateCcw className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>फिर से</span>
                    <span lang='en' className='text-xs text-muted-foreground'>Try again</span>
                </button>
            )}
        </li>
    )
}

export const PratyayaQuiz: FC<PratyayaQuizProps> = ({ root, tense, table, practiceHref }): ReactNode => {
    const questions = buildQuestions(root, tense, table)

    return (
        <div className='flex flex-col gap-4'>
            {questions.length > 0 ? (
                <ol className='flex flex-col gap-4'>
                    {questions.map((q, i) => <QuestionCard key={q.key} index={i} root={root} tense={tense} q={q} />)}
                </ol>
            ) : (
                <p className='text-sm text-muted-foreground'>
                    <span lang='hi' data-ex='hi' className='hindi'>इस काल के लिए यहाँ छोटे प्रश्न नहीं बनते; नीचे के अभ्यास खेल खोलें।</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en'>No quick questions can be made here for this tense; open the practice games below.</span>
                </p>
            )}
            <Link href={practiceHref} className='group inline-flex min-h-tap items-center gap-2 self-start rounded-full text-sm font-semibold text-saffron-strong hover:underline'>
                <span lang='hi' className='hindi'>इस काल के और प्रश्न: अभ्यास</span>
                <span aria-hidden='true'>·</span>
                <span lang='en'>More questions on this tense</span>
                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
            </Link>
        </div>
    )
}
